import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCallbackRef } from './use-callback-ref.js';

type UseCountdownTimer = {
  started?: number | null;
  lastInterval?: number | null;
  timeToCount?: number | null;
  /**
   * Remaining countdown time in milliseconds
   */
  timeLeft: number;
  /**
   * requestAnimationFrame id
   */
  requestId?: number;
};
/**
 * Dead simple yet powerful countdown hook for React. Powered by requestAnimationFrame.
 * @param timeToCount Time in milliseconds that countdown should start with. Defaults to 60000
 * @param interval Time in milliseconds representing the frequency that countdown should update with. Defaults to 1000
 * @returns
 */
export const useCountdown = (timeToCount = 60 * 1000, interval = 1000) => {
  const [timeLeft, setTimeLeft] = useState(0);
  const timer = useRef<UseCountdownTimer>({
    timeLeft: 0,
  });

  const run = useCallbackRef((ts: number) => {
    if (!timer.current.started) {
      timer.current.started = ts;
      timer.current.lastInterval = ts;
    }
    const localInterval = Math.min(
      interval,
      timer.current.timeLeft || Infinity
    );
    if (ts - timer.current.lastInterval! >= localInterval) {
      timer.current.lastInterval! += localInterval;
      setTimeLeft((timeLeft) => {
        timer.current.timeLeft = timeLeft - localInterval;
        return timer.current.timeLeft;
      });
    }

    if (ts - timer.current.started < timer.current.timeToCount!) {
      timer.current.requestId = window.requestAnimationFrame(run);
    } else {
      timer.current = {
        timeLeft: 0,
      };
      setTimeLeft(0);
    }
  });

  /**
   * Start or restart a countdown. Takes time in milliseconds to start with.
   * you can start existing timer with an arbitrary value, if new value is not passed timer will start with initial value
   */
  const start = useCallbackRef((newTimeToCount = timeToCount) => {
    if (timer.current.requestId) {
      window.cancelAnimationFrame(timer.current.requestId);
    }
    timer.current.started = null;
    timer.current.lastInterval = null;
    timer.current.timeToCount = newTimeToCount;
    timer.current.requestId = window.requestAnimationFrame(run);

    setTimeLeft(newTimeToCount);
  });

  /**
   * Pauses a countdown
   */
  const pause = useCallbackRef(() => {
    if (timer.current.requestId) {
      window.cancelAnimationFrame(timer.current.requestId);
    }
    timer.current.started = null;
    timer.current.lastInterval = null;
    timer.current.timeToCount = timer.current.timeLeft;
  });

  /**
   * Resumes a paused countdown
   */
  const resume = useCallbackRef(() => {
    if (!timer.current.started && timer.current.timeLeft > 0) {
      if (timer.current.requestId) {
        window.cancelAnimationFrame(timer.current.requestId);
      }
      timer.current.requestId = window.requestAnimationFrame(run);
    }
  });

  /**
   * Resets a countdown to initial state
   */
  const reset = useCallback(() => {
    if (timer.current.timeLeft) {
      if (timer.current.requestId) {
        window.cancelAnimationFrame(timer.current.requestId);
      }
      timer.current = {
        timeLeft: 0,
      };
      setTimeLeft(0);
    }
  }, []);

  const actions = useMemo(() => {
    return { start, pause, resume, reset };
  }, [pause, reset, resume, start]);

  useEffect(() => {
    return () => {
      if (timer.current.requestId) {
        window.cancelAnimationFrame(timer.current.requestId);
      }
    };
  }, []);

  return [timeLeft, actions] as [number, typeof actions];
};
